import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, RotateCw } from 'lucide-react';
import type { AccountPlan, BillingPlanDTO, PaidPlan, PlanUsageDTO, StoreBillingStatus, StoreOfferDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input, Separator } from '../../components/ui/Input';
import { cn } from '../../components/ui/cn';
import { formatDate } from '../../components/ui/styles';
import { formatPlanPrice } from '../billing/format';
import { IS_NATIVE, IS_WEB } from '../../lib/platform';
import { useBilling } from '../billing/useBilling';
import { plural, t, useLanguage } from '../../lib/i18n';
import {
  CONTACT_EMAIL,
  effectiveMonthlyPrice,
  formatUsd,
  planCards,
  planName,
  YEARLY_DISCOUNT,
  type PlanCard,
} from './plans';

interface SubscriptionSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Kalan gün sayısı — kota dönemi ücretli planda ödeme gününde, ücretsizde ay başında yenileniyor. */
function daysUntil(iso: string): number {
  const remaining = Date.parse(iso) - Date.now();
  return Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
}

type Interval = 'monthly' | 'yearly';

/** Fiyata göre sıra (sunucudaki plan-rules.ts › PLAN_RANK ile aynı). */
const PLAN_RANK: Record<AccountPlan, number> = { basic: 0, pro: 1, plus: 2, enterprise: 3 };

/** Abonelik satırındaki plan kodu → plan ve dönem (`onetime:pro:monthly`, `play:plus:yearly` …). */
function parsePlanReference(reference: string | undefined): { plan: PaidPlan; interval: Interval } | null {
  if (reference === 'onetime:test') return { plan: 'pro', interval: 'monthly' };
  const [prefix, plan, interval] = (reference ?? '').split(':');
  if (!['onetime', 'play', 'appstore'].includes(prefix) || (plan !== 'pro' && plan !== 'plus')) return null;
  return interval === 'monthly' || interval === 'yearly' ? { plan, interval } : null;
}

/**
 * Kupon kodu (Yönetim konsolu > Kuponlar'da oluşturulur). Plan hediyesi hemen
 * geçerli olur; indirim kuponu bir sonraki ödeme formunda uygulanır.
 */
const CouponCard: React.FC<{ onRedeemed: () => void; onNotify: (kind: ToastKind, text: string) => void }> = ({
  onRedeemed,
  onNotify,
}) => {
  const { t: translate, locale } = useLanguage();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || code.trim().length < 3) return;
    setBusy(true);
    try {
      const result = await window.zabit.redeemCoupon(code);
      setCode('');
      if (result.kind === 'plan_grant') {
        const until = result.untilISO
          ? new Date(result.untilISO).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
          : '';
        onNotify(
          'success',
          translate(`${planName(result.plan)} planın ${until} tarihine kadar açık.`, `Your ${planName(result.plan)} plan is active until ${until}.`)
        );
      } else {
        const period = result.interval === 'yearly' ? translate('yıllık', 'yearly') : translate('aylık', 'monthly');
        const pct = result.percentOff ? ` (%${result.percentOff})` : '';
        onNotify(
          'success',
          translate(
            `İndirim${pct} ${planName(result.plan)} ${period} ödemende uygulanacak.`,
            `The discount${pct} will be applied when you pay for ${planName(result.plan)} ${period}.`
          )
        );
      }
      onRedeemed();
    } catch (err) {
      onNotify('error', (err as Error).message || translate('Kupon kullanılamadı.', 'Could not redeem the coupon.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <form onSubmit={(event) => void submit(event)} className="flex flex-wrap items-center gap-3">
          <label htmlFor="coupon-code" className="text-sm font-medium">
            {translate('Kupon kodu', 'Coupon code')}
          </label>
          <Input
            id="coupon-code"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="TALKNOTE-…"
            maxLength={40}
            autoComplete="off"
            spellCheck={false}
            className="w-56 font-mono uppercase"
          />
          <Button type="submit" variant="outline" size="sm" disabled={busy || code.trim().length < 3}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {translate('Uygula', 'Apply')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

/** Kullanım çubuğu: yüzde 0–100 arasına sıkıştırılıyor. */
const UsageBar: React.FC<{ percent: number }> = ({ percent }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
    <div
      className="h-full rounded-full bg-brand transition-[width]"
      style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
    />
  </div>
);

/**
 * Ayarlar > Abonelik: aktif plan ve bu ayki kullanım, altında plan
 * karşılaştırması (bkz. plans.ts). iyzico'da tanımlı plan yoksa düğmeler
 * iletişim adresini açıyor.
 */
export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({ onNotify }) => {
  const { t: translate, locale } = useLanguage();
  const [usage, setUsage] = useState<PlanUsageDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isYearly, setIsYearly] = useState(false);
  const billing = useBilling(onNotify);
  const [isOpeningCheckout, setIsOpeningCheckout] = useState(false);

  /** Ödeme sayfasını açar (web: aynı sekme, masaüstü: tarayıcı). Bilgiler, onay ve kart orada girilir. */
  const startCheckout = async (option: BillingPlanDTO) => {
    if (!option.plan || !option.interval || isOpeningCheckout) return;
    setIsOpeningCheckout(true);
    try {
      await window.zabit.startBillingCheckout({ plan: option.plan, interval: option.interval, ...(option.test ? { test: true } : {}) });
      if (!IS_WEB) {
        onNotify(
          'info',
          translate(
            'Ödeme sayfası tarayıcıda açıldı. Ödemeyi tamamlayınca buraya dönebilirsin.',
            'The payment page opened in your browser. Come back here once you’ve finished.'
          )
        );
      }
    } catch (err) {
      onNotify('error', (err as Error).message || translate('Ödeme başlatılamadı.', 'Could not start the payment.'));
    } finally {
      setIsOpeningCheckout(false);
    }
  };

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setUsage(await window.zabit.getPlanUsage());
    } catch (err) {
      setLoadError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const currentPlan: AccountPlan = usage?.plan ?? 'basic';
  const cards = planCards();

  // iyzico'da tanımlı plan (sunucudaki IYZICO_PLAN_REF_*). Yoksa kart iletişim adresine yönlendiriyor.
  // Mobil uygulamada dijital abonelik yalnızca mağazanın ödeme sistemiyle satılabiliyor
  // (docs/mobil-uygulama-plani.md, Faz 2): orada iyzico ödemesi ve kupon yok.
  const allOptions = IS_NATIVE ? [] : (billing.status.data?.plans ?? []);
  const purchasable = allOptions.filter((option) => !option.test);
  const testOption = allOptions.find((option) => option.test);
  const hasCheckout = purchasable.length > 0;
  // Tek seferlik ödeme (iyzico Abonelik eklentisi yokken): plan ödenen süre kadar, yenilenmez.
  const oneTimeCheckout = purchasable.some((option) => option.renews === false);
  const prepaidUntilISO =
    billing.subscription?.status === 'PREPAID' &&
    billing.subscription.currentPeriodEndISO &&
    Date.parse(billing.subscription.currentPeriodEndISO) > Date.now()
      ? billing.subscription.currentPeriodEndISO
      : null;
  const optionFor = (plan: AccountPlan, interval: Interval = isYearly ? 'yearly' : 'monthly'): BillingPlanDTO | undefined =>
    purchasable.find((option) => option.plan === plan && option.interval === interval);
  // Şimdiki plan ve dönem, dönem sonunda başlayacak plan (alt plana geçiş): düğmeler buna göre.
  const currentRef = parsePlanReference(billing.subscription?.planReference);
  const scheduledRef = parsePlanReference(billing.subscription?.scheduledReference);
  const paidEndISO = billing.subscription?.currentPeriodEndISO;

  // Mobil: mağazadaki (Google Play / App Store) teklifler ve bu telefonun mağaza hesabındaki abonelik.
  const [store, setStore] = useState<StoreBillingStatus | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const loadStore = useCallback(async () => {
    const storeBilling = window.zabit.storeBilling;
    if (!storeBilling) return;
    setStore(await storeBilling.getStatus().catch(() => ({ storeName: '', offers: [], ownedProductIds: [] })));
  }, []);
  useEffect(() => {
    void loadStore();
  }, [loadStore]);
  const storeOffers = store?.offers ?? [];
  const storeName = store?.storeName ?? '';
  const ownsStoreSubscription = (store?.ownedProductIds.length ?? 0) > 0;
  const storeOfferFor = (plan: AccountPlan): StoreOfferDTO | undefined =>
    storeOffers.find((offer) => offer.plan === plan && offer.interval === (isYearly ? 'yearly' : 'monthly'));

  // Hesabın aboneliği bir mağazadan mı (başka bir telefonda alınmış da olabilir)? Mağaza
  // aboneliği yalnızca mağazadan yönetilir: iyzico ödeme, iptal ve tazeleme ona göre gizli.
  const subscription = billing.subscription;
  const subscriptionStore =
    subscription?.provider === 'google_play' ? 'Google Play' : subscription?.provider === 'app_store' ? 'App Store' : null;
  const storeManaged =
    subscriptionStore !== null &&
    subscription !== undefined &&
    (subscription.status === 'ON_HOLD' ||
      subscription.status === 'PAUSED' ||
      (subscription.status !== 'REVOKED' &&
        subscription.status !== 'EXPIRED' &&
        Boolean(subscription.currentPeriodEndISO && Date.parse(subscription.currentPeriodEndISO) > Date.now())));
  const iyzicoSubscription = subscriptionStore ? undefined : subscription;
  // Mobil: bu telefonun mağazasındaki abonelik uygulamadan başka planla değiştirilebilir.
  const canSwitchInStore = IS_NATIVE && storeManaged && subscriptionStore === storeName && currentPlan !== 'basic';

  /** Mağazadan satın alır (ya da süren mağaza aboneliğini değiştirir); sunucu doğrulayınca plan açılır. */
  const buyInStore = async (offer: StoreOfferDTO, replace = false) => {
    const storeBilling = window.zabit.storeBilling;
    if (!storeBilling || isPurchasing) return;
    setIsPurchasing(true);
    try {
      const result = await storeBilling.purchase(offer, { replace, ...(replace && currentRef ? { from: currentRef } : {}) });
      if (result.ok && result.plan !== offer.plan) {
        // Alt plana geçiş (iki mağazada da) dönem sonunda geçerli oluyor.
        onNotify(
          'info',
          translate(
            `${planName(offer.plan)} planına geçişin bir sonraki yenilemede geçerli olacak.`,
            `Your switch to ${planName(offer.plan)} takes effect at the next renewal.`
          )
        );
        billing.reload();
        void loadStore();
      } else if (result.ok) {
        onNotify('success', translate(`${planName(offer.plan)} planın açıldı.`, `Your ${planName(offer.plan)} plan is active.`));
        void load();
        billing.reload();
        void loadStore();
      } else if (result.pending) {
        onNotify(
          'info',
          translate('Ödeme bekleniyor. Tamamlanınca planın kendiliğinden açılır.', 'Payment is pending. Your plan opens automatically once it completes.')
        );
      } else if (!result.cancelled && result.error) {
        onNotify('error', result.error);
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  /** Basic kartında düğme yerine: ücretli plandan ücretsize ne zaman ve nasıl dönülür. */
  const basicNote = (): string => {
    if (storeManaged) {
      return translate(
        `Ücretsiz plana dönmek için aboneliği ${subscriptionStore} › Abonelikler'den iptal et; dönem sonuna kadar kullanmaya devam edersin.`,
        `To go back to the free plan, cancel under ${subscriptionStore} › Subscriptions; you keep your plan until the period ends.`
      );
    }
    if (billing.isSubscribed) {
      return translate(
        'Aboneliği aşağıdan iptal edersen dönem sonunda ücretsiz plana dönersin.',
        'Cancel your subscription below to go back to the free plan at the end of the period.'
      );
    }
    const until = billing.subscription?.scheduledPeriodEndISO ?? prepaidUntilISO ?? usage?.planUntilISO;
    return until
      ? translate(
          `Ödediğin süre ${formatDate(until)} tarihinde bitince ücretsiz plana kendiliğinden dönersin.`,
          `You go back to the free plan automatically when your paid time ends on ${formatDate(until)}.`
        )
      : translate('Ücretli planın bitince ücretsiz plana kendiliğinden dönersin.', 'You go back to the free plan automatically when your paid plan ends.');
  };

  /**
   * Web, ödenmiş dönem sürerken başka ücretli plan: üst plana yükseltme hemen (kalan günlerin
   * farkı, bitiş aynı), alt plana geçiş dönem sonunda. Tutar ve tarih ödeme sayfasında.
   */
  const prepaidChangeButton = (card: PlanCard) => {
    if (scheduledRef) {
      return (
        <p className="text-center text-[0.8rem] text-muted-foreground">
          {translate('Sıradaki planın başlayınca yeniden değiştirebilirsin.', 'You can change again once your next plan starts.')}
        </p>
      );
    }
    const upgrade = PLAN_RANK[card.id] > PLAN_RANK[currentPlan];
    // Yıllık plan yalnızca yıllık olarak yükseltilir (sunucu kuralı); ödeme sayfası yıllığı seçer.
    const option = upgrade && currentRef?.interval === 'yearly' ? optionFor(card.id, 'yearly') : optionFor(card.id);
    if (!option) return null;
    return (
      <>
        <Button className="w-full" variant={upgrade ? 'default' : 'outline'} disabled={isOpeningCheckout} onClick={() => void startCheckout(option)}>
          {upgrade ? translate(`${card.name} planına yükselt`, `Upgrade to ${card.name}`) : translate(`${card.name} planına geç`, `Switch to ${card.name}`)}
        </Button>
        <p className="text-center text-[0.8rem] text-muted-foreground">
          {upgrade
            ? translate(
                'Hemen geçer; yalnızca kalan günlerin farkını ödersin, bitiş tarihin değişmez.',
                'Applies now; you pay only the difference for the days left and your end date stays the same.'
              )
            : translate(
                `${formatDate(prepaidUntilISO!)} tarihinde, ${planName(currentPlan)} planın bitince başlar.`,
                `Starts on ${formatDate(prepaidUntilISO!)}, when your ${planName(currentPlan)} plan ends.`
              )}
        </p>
      </>
    );
  };

  const contact = (plan: PlanCard) => {
    const subject = translate(`TalkNote ${plan.name} planı`, `TalkNote ${plan.name} plan`);
    const opened = window.zabit.openExternal(`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`);
    void Promise.resolve(opened).catch(() => {
      onNotify('info', translate(`Bize ulaş: ${CONTACT_EMAIL}`, `Get in touch: ${CONTACT_EMAIL}`));
    });
  };

  return (
    <div className="space-y-8">
      {/* — Aktif plan — */}
      <section className="space-y-2.5">
        <h2 className="text-sm font-medium text-muted-foreground">{translate('Aktif plan', 'Active plan')}</h2>

        {!usage ? (
          <Card>
            <CardContent>
              {loadError ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-destructive">{loadError}</p>
                  <Button variant="outline" size="sm" onClick={() => void load()}>
                    <RotateCw className="size-4" />
                    {translate('Tekrar dene', 'Try again')}
                  </Button>
                </div>
              ) : (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  {translate('Plan yükleniyor…', 'Loading your plan…')}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg">
                  {translate(`${planName(usage.plan)} planı`, `${planName(usage.plan)} plan`)}
                </CardTitle>
                <span className="text-right text-sm text-muted-foreground">
                  {usage.planUntilISO ? (
                    <span className="block">
                      {translate(
                        `${usage.planSource === 'coupon' ? 'Kupon' : 'Hediye'}: ${new Date(usage.planUntilISO).toLocaleDateString(locale, { day: 'numeric', month: 'long' })} tarihine kadar`,
                        `${usage.planSource === 'coupon' ? 'Coupon' : 'Gift'}: until ${new Date(usage.planUntilISO).toLocaleDateString(locale, { day: 'numeric', month: 'long' })}`
                      )}
                    </span>
                  ) : null}
                  {translate(
                    `${daysUntil(usage.periodEndISO)} gün sonra yenilenir`,
                    `Resets in ${plural(daysUntil(usage.periodEndISO), 'day', 'days')}`
                  )}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Separator />

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span>{translate('Kayıt süresi', 'Recording time')}</span>
                  <span className="text-muted-foreground">
                    {translate(
                      `${usage.usedMinutes} / ${usage.quotaMinutes} dk`,
                      `${usage.usedMinutes} / ${usage.quotaMinutes} min`
                    )}
                  </span>
                </div>
                <UsageBar percent={usage.quotaMinutes > 0 ? (usage.usedMinutes / usage.quotaMinutes) * 100 : 0} />
              </div>

              <div className="flex items-center justify-between gap-4 text-sm">
                <span>{translate('Bu dönemki toplantılar', 'Meetings this period')}</span>
                <span className="text-muted-foreground">
                  {translate(`${usage.meetingCount} toplantı`, plural(usage.meetingCount, 'meeting', 'meetings'))}
                </span>
              </div>

              <p className="text-[0.8rem] text-muted-foreground">
                {translate(
                  `Dönem ${new Date(usage.periodStartISO).toLocaleDateString(locale, { day: 'numeric', month: 'long' })} tarihinde başladı. Silinen toplantılar kullanılan süreyi geri vermez.`,
                  `The period started on ${new Date(usage.periodStartISO).toLocaleDateString(locale, { day: 'numeric', month: 'long' })}. Deleting meetings does not give back used time.`
                )}
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* — Kupon — */}
      {billing.status.data?.configured && !IS_NATIVE ? (
        <section className="space-y-2.5">
          <CouponCard
            onNotify={onNotify}
            onRedeemed={() => {
              void load();
              billing.reload();
            }}
          />
        </section>
      ) : null}

      {/* — Plan karşılaştırması — */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">
            {translate('Planları karşılaştır', 'Compare plans')}
          </h2>

          {/* Aylık / yıllık seçimi */}
          <div className="inline-flex items-center rounded-full border border-border bg-card p-1 text-sm">
            {[false, true].map((yearly) => (
              <button
                key={String(yearly)}
                type="button"
                onClick={() => setIsYearly(yearly)}
                aria-pressed={isYearly === yearly}
                className={cn(
                  'rounded-full px-3 py-1 transition-colors',
                  isYearly === yearly
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {yearly ? translate('Yıllık', 'Yearly') : translate('Aylık', 'Monthly')}
                {yearly && (
                  <span className={cn('ml-1.5 text-xs', isYearly ? 'text-primary-foreground/80' : 'text-emerald-500')}>
                    {translate(`%${YEARLY_DISCOUNT * 100} indirim`, `Save ${YEARLY_DISCOUNT * 100}%`)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className={'grid gap-4 sm:grid-cols-2 xl:grid-cols-4'}>
          {cards.map((card) => {
            const isCurrent = card.id === currentPlan;
            const option = optionFor(card.id);
            const storeOffer = storeOfferFor(card.id);
            const price = card.monthlyPrice === null ? null : effectiveMonthlyPrice(card.monthlyPrice, isYearly);
            return (
              <Card key={card.id} className={cn('gap-4', isCurrent && 'border-brand')}>
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-lg">{card.name}</CardTitle>
                    {isCurrent && (
                      <span className="rounded-full border border-brand/40 bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                        {translate('Mevcut', 'Current')}
                      </span>
                    )}
                  </div>
                  <Separator />
                  {storeOffer ? (
                    // Mobil: mağazanın kendi (yerel para birimindeki) fiyatı.
                    <div>
                      <p className="text-3xl font-bold tracking-tight">{storeOffer.priceString}</p>
                      <p className="text-[0.8rem] text-muted-foreground">
                        {storeOffer.interval === 'yearly'
                          ? translate(`yıllık · ${storeName}`, `per year · ${storeName}`)
                          : translate(`aylık · ${storeName}`, `per month · ${storeName}`)}
                      </p>
                    </div>
                  ) : (
                  <div>
                    {/* Fiyat hep dolar (liste fiyatı); tahsilat para birimi farklıysa (TL, günlük TCMB kuru) altta not. */}
                    <p className="flex items-start gap-2 text-3xl font-bold tracking-tight">
                      {price === null ? translate('Teklif', 'Custom') : price === 0 ? translate('Ücretsiz', 'Free') : formatUsd(price)}
                      {price !== null && price > 0 && isYearly && card.monthlyPrice ? (
                        <s className="mt-1 text-base font-medium text-muted-foreground">{formatUsd(card.monthlyPrice)}</s>
                      ) : null}
                    </p>
                    <p className="text-[0.8rem] text-muted-foreground">
                      {price !== null && price > 0 && isYearly ? translate('aylık, yıllık ödenir', 'per month, billed yearly') : card.priceNote}
                    </p>
                    {option && option.currencyCode !== 'USD' ? (
                      <p className="mt-1 text-[0.75rem] text-muted-foreground">
                        {option.interval === 'yearly'
                          ? translate(
                              `Yıllık ${formatPlanPrice(option)} olarak tahsil edilir (günün TCMB kuru)`,
                              `Charged as ${formatPlanPrice(option)} per year (today's exchange rate)`
                            )
                          : translate(
                              `${formatPlanPrice(option)} olarak tahsil edilir (günün TCMB kuru)`,
                              `Charged as ${formatPlanPrice(option)} (today's exchange rate)`
                            )}
                      </p>
                    ) : null}
                  </div>
                  )}
                </CardHeader>

                <CardContent className="space-y-3">
                  {isCurrent ? (
                    <>
                      <Button variant="secondary" disabled className="w-full">
                        {translate('Mevcut planın', 'Your current plan')}
                      </Button>
                      {/* Mobil: iptal ve dönem değişikliği mağazanın Abonelikler ekranında. */}
                      {IS_NATIVE && (ownsStoreSubscription || (storeManaged && subscriptionStore === storeName)) && (
                        <Button variant="outline" className="w-full" onClick={() => void window.zabit.storeBilling?.manage()}>
                          {translate('Aboneliği yönet', 'Manage subscription')}
                        </Button>
                      )}
                      {/* İptal edilmiş abonelik ödenmiş dönem sonuna kadar sürer (iyzico iade yapmaz). */}
                      {billing.subscription?.status === 'CANCELED' &&
                        billing.subscription.currentPeriodEndISO &&
                        Date.parse(billing.subscription.currentPeriodEndISO) > Date.now() && (
                          <p className="text-center text-[0.8rem] text-muted-foreground">
                            {translate(
                              `Abonelik iptal edildi · ${formatDate(billing.subscription.currentPeriodEndISO)} tarihinde ücretsiz plana geçersin`,
                              `Subscription canceled · you move to the free plan on ${formatDate(billing.subscription.currentPeriodEndISO)}`
                            )}
                          </p>
                        )}
                      {/* Tek seferlik ödeme: dönem sonunda biter; aynı plan önceden uzatılabilir (süre üstüne eklenir). */}
                      {prepaidUntilISO && (
                        <>
                          <p className="text-center text-[0.8rem] text-muted-foreground">
                            {translate(
                              `${formatDate(prepaidUntilISO)} tarihine kadar geçerli · otomatik yenilenmez`,
                              `Valid until ${formatDate(prepaidUntilISO)} · doesn’t renew automatically`
                            )}
                          </p>
                          {option && !scheduledRef && (
                            <Button variant="outline" className="w-full" disabled={isOpeningCheckout} onClick={() => void startCheckout(option)}>
                              {currentRef && currentRef.interval !== option.interval
                                ? option.interval === 'yearly'
                                  ? translate('Yıllık ödemeye geç', 'Switch to yearly')
                                  : translate('Bitince aylıkla devam et', 'Continue monthly when it ends')
                                : option.interval === 'yearly'
                                  ? translate('1 yıl uzat', 'Extend by 1 year')
                                  : translate('1 ay uzat', 'Extend by 1 month')}
                            </Button>
                          )}
                        </>
                      )}
                      {/* Dönem sonunda başlayacak plan (alt plana ya da yıllıktan aylığa geçiş). */}
                      {scheduledRef && paidEndISO && (
                        <p className="text-center text-[0.8rem] text-muted-foreground">
                          {translate(
                            `Sıradaki plan: ${planName(scheduledRef.plan)} ${scheduledRef.interval === 'yearly' ? 'yıllık' : 'aylık'} · ${formatDate(paidEndISO)} tarihinde başlar`,
                            `Next plan: ${planName(scheduledRef.plan)} ${scheduledRef.interval === 'yearly' ? 'yearly' : 'monthly'} · starts on ${formatDate(paidEndISO)}`
                          )}
                        </p>
                      )}
                    </>
                  ) : card.id === 'basic' ? (
                    // Ücretsiz plana geçiş düğmesi yok: ücretli plan bitince (ya da iptal edilince) kendiliğinden.
                    <p className="text-center text-[0.8rem] text-muted-foreground">{basicNote()}</p>
                  ) : IS_NATIVE && card.id !== 'enterprise' ? (
                    // Mobil: ücretsiz plandan satın alma; bu mağazadaki abonelikte plan değiştirme
                    // (üst plan hemen, alt plan dönem sonunda — mağazanın kuralı).
                    storeOffer && (currentPlan === 'basic' || canSwitchInStore) ? (
                      <>
                        <Button className="w-full" disabled={isPurchasing} onClick={() => void buyInStore(storeOffer, currentPlan !== 'basic')}>
                          {isPurchasing && <Loader2 className="size-4 animate-spin" />}
                          {currentPlan !== 'basic' && PLAN_RANK[card.id] > PLAN_RANK[currentPlan]
                            ? translate(`${card.name} planına yükselt`, `Upgrade to ${card.name}`)
                            : translate(`${card.name} planına geç`, `Switch to ${card.name}`)}
                        </Button>
                        {currentPlan !== 'basic' && (
                          <p className="text-center text-[0.8rem] text-muted-foreground">
                            {PLAN_RANK[card.id] > PLAN_RANK[currentPlan]
                              ? translate('Hemen geçer; kalan günlerin farkını ödersin.', 'Applies now; you pay the difference for the days left.')
                              : translate('Şimdiki dönemin bitince başlar.', 'Starts when your current period ends.')}
                          </p>
                        )}
                      </>
                    ) : prepaidUntilISO && card.id !== currentPlan ? (
                      <p className="text-center text-[0.8rem] text-muted-foreground">
                        {translate(
                          `Planın ${formatDate(prepaidUntilISO)} tarihine kadar geçerli; sonra buradan seçebilirsin.`,
                          `Your plan is valid until ${formatDate(prepaidUntilISO)}; you can pick a plan here after that.`
                        )}
                      </p>
                    ) : null
                  ) : prepaidUntilISO && card.id !== 'enterprise' && card.monthlyPrice !== null ? (
                    // Web: ödenmiş dönem sürerken plan değiştirme — üst plan hemen (fark), alt plan dönem sonunda.
                    prepaidChangeButton(card)
                  ) : storeManaged && card.id !== 'enterprise' ? (
                    // Web/masaüstü: mağaza aboneliği varken iyzico'dan satın alınamaz (sunucu da reddeder).
                    <p className="text-center text-[0.8rem] text-muted-foreground">
                      {translate(`Plan değişikliği ${subscriptionStore} üzerinden`, `Plan changes go through ${subscriptionStore}`)}
                    </p>
                  ) : (
                    <Button
                      variant={card.id === 'enterprise' ? 'outline' : 'default'}
                      className="w-full"
                      disabled={isOpeningCheckout && Boolean(option)}
                      onClick={() => (option ? void startCheckout(option) : contact(card))}
                    >
                      {card.id === 'enterprise'
                        ? translate('Bize ulaşın', 'Contact us')
                        : translate(`${card.name} planına geç`, `Switch to ${card.name}`)}
                    </Button>
                  )}

                  <p className="text-sm font-medium">{card.featuresTitle}</p>
                  <ul className="space-y-2">
                    {card.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-[0.8rem] leading-relaxed">
                        <Check className="mt-0.5 size-3.5 shrink-0 text-brand" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-[0.8rem] text-muted-foreground">
          {IS_NATIVE
            ? storeOffers.length === 0
              ? translate(
                  `Uygulama içinden plan satın alma yakında. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                  `In-app plan purchases are coming soon. For Enterprise and questions: ${CONTACT_EMAIL}.`
                )
              : currentPlan === 'basic'
                ? translate(
                    `Ödeme ${storeName} üzerinden alınır; abonelik her dönem kendiliğinden yenilenir, ${storeName} › Abonelikler'den istediğin zaman iptal edebilirsin. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                    `Payment is taken by ${storeName}; the subscription renews every period and you can cancel any time under ${storeName} › Subscriptions. For Enterprise and questions: ${CONTACT_EMAIL}.`
                  )
                : canSwitchInStore
                  ? translate(
                      `Plan değişikliği ${storeName} üzerinden: üst plana geçiş hemen olur, yalnızca kalan günlerin farkını ödersin; alt plana geçiş şimdiki dönemin sonunda başlar. İptal için ${storeName} › Abonelikler. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                      `Plan changes go through ${storeName}: upgrades apply right away and you pay only the difference for the days left; downgrades start at the end of the current period. To cancel: ${storeName} › Subscriptions. For Enterprise and questions: ${CONTACT_EMAIL}.`
                    )
                  : storeManaged
                    ? translate(
                        `Aboneliğin ${subscriptionStore} üzerinden; değişiklik ve iptal oradan, ${subscriptionStore} › Abonelikler'den yapılır. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                        `Your subscription is through ${subscriptionStore}; change or cancel it under ${subscriptionStore} › Subscriptions. For Enterprise and questions: ${CONTACT_EMAIL}.`
                      )
                    : translate(
                        `Bu plan uygulama içinden değiştirilemiyor; süresi bitince buradan ${storeName || 'mağaza'} ile plan seçebilirsin. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                        `This plan can’t be changed in the app; when it ends you can pick a plan here through ${storeName || 'the store'}. For Enterprise and questions: ${CONTACT_EMAIL}.`
                      )
            : storeManaged
              ? translate(
                  `Aboneliğin ${subscriptionStore} üzerinden alındı; plan değişikliği mobil uygulamadan, iptal ${subscriptionStore} › Abonelikler'den yapılır. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                  `Your subscription was bought through ${subscriptionStore}; change plans in the mobile app and cancel under ${subscriptionStore} › Subscriptions. For Enterprise and questions: ${CONTACT_EMAIL}.`
                )
            : hasCheckout
            ? oneTimeCheckout
              ? translate(
                  `Ödeme iyzico'nun güvenli sayfasında, tek seferlik yapılır: plan ödediğin süre (1 ay ya da 1 yıl) boyunca geçerlidir, otomatik yenilenmez. Üst plana geçiş hemen olur, yalnızca kalan günlerin farkını ödersin; alt plana geçiş ödediğin dönem bitince başlar. Kayıt kotası ödeme gününde yenilenir. Kart bilgin TalkNote'a gelmez. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                  `Payment is one-off on iyzico’s secure page: the plan is valid for the period you pay for (1 month or 1 year) and doesn’t renew automatically. Upgrades apply right away and you pay only the difference for the days left; downgrades start when your paid period ends. Your recording quota resets on your payment day. TalkNote never sees your card. For Enterprise and questions: ${CONTACT_EMAIL}.`
                )
              : translate(
                  `Ödeme iyzico'nun güvenli sayfasında yapılır, kart bilgin TalkNote'a gelmez. Kurumsal plan ve sorular için ${CONTACT_EMAIL}.`,
                  `Payment happens on iyzico’s secure page; TalkNote never sees your card. For Enterprise and questions: ${CONTACT_EMAIL}.`
                )
            : translate(
                `Fiyatlar ve plan içerikleri şimdilik geçici. Ödeme akışı bağlanana kadar plan değişikliği için ${CONTACT_EMAIL} adresine yazabilirsin.`,
                `Prices and plan contents are placeholders for now. Until checkout is connected, write to ${CONTACT_EMAIL} to change your plan.`
              )}
        </p>

        {/* Yalnızca yöneticiye: gerçek kartla 1 TL'lik deneme (1 ay Pro) — ödeme akışını uçtan uca sınamak için. */}
        {testOption && (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[0.8rem] text-muted-foreground">
                {translate(
                  `Test ödemesi (yalnızca yöneticiler görür): ${formatPlanPrice(testOption)} ile 1 aylık Pro.`,
                  `Test payment (admins only): 1 month of Pro for ${formatPlanPrice(testOption)}.`
                )}
              </p>
              <Button variant="outline" size="sm" disabled={isOpeningCheckout} onClick={() => void startCheckout(testOption)}>
                {translate('Test ödemesi yap', 'Make a test payment')}
              </Button>
            </CardContent>
          </Card>
        )}
      </section>

      {/* — Ödeme (iyzico) — mobilde yok: orada abonelik mağazada, yönetimi plan kartında. */}
      {!IS_NATIVE && (
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">{translate('Ödeme', 'Payment')}</h2>

        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="font-medium leading-none">{storeManaged ? subscriptionStore : 'iyzico'}</p>
              <p className="text-[0.8rem] text-muted-foreground">
                {billing.status.isLoading && !billing.status.data
                  ? translate('Durum yükleniyor…', 'Loading status…')
                  : storeManaged && subscription
                    ? subscription.status === 'ON_HOLD' || subscription.status === 'PAUSED'
                      ? subscription.status === 'PAUSED'
                        ? translate(`Duraklatıldı · ${subscriptionStore} › Abonelikler'den sürdürebilirsin`, `Paused · resume it under ${subscriptionStore} › Subscriptions`)
                        : translate(
                            `Ödeme alınamadı · ${subscriptionStore} › Abonelikler'den ödeme yöntemini güncelle`,
                            `Payment failed · update your payment method under ${subscriptionStore} › Subscriptions`
                          )
                      : subscription.status === 'CANCELED' && subscription.currentPeriodEndISO
                        ? translate(
                            `İptal edildi · ${formatDate(subscription.currentPeriodEndISO)} tarihine kadar kullanabilirsin`,
                            `Canceled · you can use it until ${formatDate(subscription.currentPeriodEndISO)}`
                          )
                        : subscription.currentPeriodEndISO
                          ? translate(
                              `Aktif · ${formatDate(subscription.currentPeriodEndISO)} tarihinde ${subscriptionStore} yeniler`,
                              `Active · ${subscriptionStore} renews it on ${formatDate(subscription.currentPeriodEndISO)}`
                            )
                          : translate('Aktif', 'Active')
                  : !billing.status.data?.configured
                    ? translate('Ödeme sağlayıcısı henüz bağlanmadı.', 'The payment provider isn’t connected yet.')
                    : billing.isSubscribed
                      ? billing.subscription?.currentPeriodEndISO
                        ? translate(
                            `Aktif · ${formatDate(billing.subscription.currentPeriodEndISO)} tarihinde yenilenir`,
                            `Active · renews on ${formatDate(billing.subscription.currentPeriodEndISO)}`
                          )
                        : translate('Aktif', 'Active')
                      : prepaidUntilISO
                        ? translate(
                            `Ödendi · ${formatDate(prepaidUntilISO)} tarihine kadar geçerli, otomatik yenilenmez`,
                            `Paid · valid until ${formatDate(prepaidUntilISO)}, doesn’t renew automatically`
                          )
                      : billing.subscription?.status === 'CANCELED' &&
                          billing.subscription.currentPeriodEndISO &&
                          Date.parse(billing.subscription.currentPeriodEndISO) > Date.now()
                        ? translate(
                            `İptal edildi · ${formatDate(billing.subscription.currentPeriodEndISO)} tarihine kadar kullanabilirsin`,
                            `Canceled · you can use it until ${formatDate(billing.subscription.currentPeriodEndISO)}`
                          )
                      : iyzicoSubscription
                        ? iyzicoSubscription.status
                        : translate('Kart bilgisi yok, abonelik başlatılmadı.', 'No card on file, no subscription started.')}
              </p>
            </div>

            {/* Mağaza aboneliğinin iptali ve ödemesi mağazada; burada düğme yok. */}
            {!storeManaged && (
            <div className="flex shrink-0 items-center gap-2">
              {iyzicoSubscription && (
                <Button
                  variant="outline"
                  size="icon"
                  disabled={billing.isBusy}
                  title={translate('Durumu iyzico’dan tazele', 'Refresh status from iyzico')}
                  aria-label={translate('Durumu iyzico’dan tazele', 'Refresh status from iyzico')}
                  onClick={() => void billing.refresh()}
                >
                  <RotateCw className={billing.isBusy ? 'size-4 animate-spin' : 'size-4'} />
                </Button>
              )}
              {/* Abonelik yukarıdaki plan kartlarından başlatılıyor. */}
              {iyzicoSubscription?.status === 'ACTIVE' && (
                <Button variant="outline" disabled={billing.isBusy} onClick={() => void billing.cancel()}>
                  {translate('Aboneliği iptal et', 'Cancel subscription')}
                </Button>
              )}
            </div>
            )}
          </CardContent>
        </Card>

        {billing.status.data?.planError && (
          <p className="text-[0.8rem] text-amber-500">{billing.status.data.planError}</p>
        )}
      </section>
      )}
    </div>
  );
};
